import { useState } from 'react';

interface BrandMarkProps {
    /** The restaurant's light-surface logo. Null when they have not uploaded one. */
    logoUrl: string | null;
    /** Shown when there is no logo, and used as the logo's accessible name when there is. */
    name: string;
    /** Extra classes for the image, for screens that want a different size or alignment. */
    className?: string;
}

/**
 * The restaurant's logo, or its name when there is no logo.
 *
 * Deliberately renders no heading of its own. Every screen that shows this already has an <h1> — the check
 * screen's names the restaurant, the receipt's announces the payment — and a second one here would either
 * duplicate that or bury it. The caller decides what the heading is; this only decides what identity looks
 * like, so all the screens agree.
 *
 * Falls back to the name when the image fails to load, not only when the URL is absent. A guest on restaurant
 * wifi is exactly the person whose image request times out, and a broken-image icon where a restaurant's
 * identity should be is worse than its name in type.
 *
 * The logo replaces the name rather than sitting beside it — the logo already says who this is, and showing
 * both reads as a fallback that failed to resolve.
 */
export function BrandMark({ logoUrl, name, className }: BrandMarkProps) {
    const [failed, setFailed] = useState(false);

    if (!logoUrl || failed) return <>{name}</>;

    return (
        <img
            src={logoUrl}
            alt={name}
            onError={() => setFailed(true)}
            className={className ?? 'h-10 w-auto max-w-[70%] object-contain object-left'}
        />
    );
}

